import type { Editor } from '@tiptap/core';
import { store } from '@/state/store';
import {
  listChanges, acceptChange, rejectChange,
  acceptAllChanges, rejectAllChanges,
  type TrackedChange,
} from '@/editor/trackChangesService';

// ═══════════════════════════════════════════════════════════════
// Panel revizí (Fáze 3) — seznam sledovaných změn s možností je
// přijmout nebo odmítnout, jako podokno revizí ve Wordu.
// ═══════════════════════════════════════════════════════════════

export class ReviewPanel {
  private root: HTMLElement;
  private editor: Editor;
  private listEl: HTMLElement;
  private open = false;
  private unsub: () => void;

  /** Volané při otevření/zavření — ribbon podle toho zvýrazní tlačítko. */
  onVisibilityChange?: (open: boolean) => void;

  constructor(container: HTMLElement, editor: Editor) {
    this.editor = editor;

    this.root = document.createElement('aside');
    this.root.className = 'review-panel';
    this.root.innerHTML = `
      <div class="review-head">
        <span class="review-title">Revize</span>
        <div class="review-head-actions">
          <button class="review-menu-btn" title="Další akce">⋯</button>
          <div class="review-menu" hidden>
            <button class="review-menu-item" data-action="accept-all">Přijmout všechny</button>
            <button class="review-menu-item" data-action="reject-all">Odmítnout všechny</button>
          </div>
          <button class="review-close" title="Zavřít panel">×</button>
        </div>
      </div>
      <div class="review-list"></div>`;
    container.appendChild(this.root);

    this.listEl = this.root.querySelector('.review-list')!;
    this.root.querySelector('.review-close')!.addEventListener('click', () => this.hide());
    this.setupMenu();

    // Změna dokumentu → překresli seznam (pozice i obsah se mění).
    editor.on('update', () => this.render());
    this.unsub = store.subscribe(() => this.render());

    this.render();
  }

  show(): void {
    this.open = true;
    this.root.classList.add('is-open');
    this.onVisibilityChange?.(true);
    this.render();
  }

  hide(): void {
    this.open = false;
    this.root.classList.remove('is-open');
    this.onVisibilityChange?.(false);
  }

  toggle(): void {
    this.open ? this.hide() : this.show();
  }

  private setupMenu(): void {
    const btn = this.root.querySelector('.review-menu-btn') as HTMLButtonElement;
    const menu = this.root.querySelector('.review-menu') as HTMLElement;

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const n = listChanges(this.editor).length;
      menu.querySelectorAll('.review-menu-item').forEach((i) => {
        (i as HTMLButtonElement).disabled = n === 0;
      });
      const acc = menu.querySelector('[data-action="accept-all"]') as HTMLButtonElement;
      const rej = menu.querySelector('[data-action="reject-all"]') as HTMLButtonElement;
      acc.textContent = n ? `Přijmout všechny (${n})` : 'Přijmout všechny';
      rej.textContent = n ? `Odmítnout všechny (${n})` : 'Odmítnout všechny';
      menu.hidden = !menu.hidden;
    });

    document.addEventListener('click', (e) => {
      if (!menu.hidden && !menu.contains(e.target as HTMLElement) && e.target !== btn) {
        menu.hidden = true;
      }
    });

    menu.addEventListener('click', (e) => {
      const item = (e.target as HTMLElement).closest('.review-menu-item') as HTMLButtonElement;
      if (!item || item.disabled) return;
      menu.hidden = true;
      const n = listChanges(this.editor).length;
      if (!n) return;

      if (item.dataset.action === 'accept-all') {
        if (!confirm(`Přijmout všech ${n} ${pluralChanges(n)}?\n\nZměny se natrvalo zapíší do dokumentu.`)) return;
        acceptAllChanges(this.editor);
      } else {
        if (!confirm(`Odmítnout všech ${n} ${pluralChanges(n)}?\n\nDokument se vrátí do původního stavu.`)) return;
        rejectAllChanges(this.editor);
      }
      this.render();
    });
  }

  private render(): void {
    if (!this.open) return; // zavřený panel nemá smysl překreslovat
    const changes = listChanges(this.editor);
    this.listEl.innerHTML = '';

    if (!changes.length) {
      const empty = document.createElement('div');
      empty.className = 'review-empty';
      empty.textContent = 'Žádné sledované změny. Zapněte Sledování změn a upravte text.';
      this.listEl.appendChild(empty);
      return;
    }

    for (const c of changes) {
      this.listEl.appendChild(this.renderItem(c));
    }
  }

  private renderItem(c: TrackedChange): HTMLElement {
    const el = document.createElement('div');
    el.className = 'review-item';

    const isIns = c.kind === 'insertion';
    el.innerHTML = `
      <div class="review-meta">
        <span class="review-avatar" style="background:${authorColor(c.author)}">${escapeHtml(initialsFrom(c.author))}</span>
        <span class="review-author">${escapeHtml(c.author)}</span>
        <span class="review-kind ${isIns ? 'is-ins' : 'is-del'}">${isIns ? 'vloženo' : 'smazáno'}</span>
        <span class="review-date">${escapeHtml(formatDate(c.date))}</span>
      </div>
      <div class="review-text ${isIns ? 'is-ins' : 'is-del'}">${escapeHtml(truncate(c.text, 120))}</div>`;

    const actions = document.createElement('div');
    actions.className = 'review-actions';

    const acc = document.createElement('button');
    acc.className = 'review-btn review-btn-accept';
    acc.textContent = 'Přijmout';
    acc.addEventListener('click', (e) => {
      e.stopPropagation();
      acceptChange(this.editor, c);
      this.render();
    });

    const rej = document.createElement('button');
    rej.className = 'review-btn review-btn-reject';
    rej.textContent = 'Odmítnout';
    rej.addEventListener('click', (e) => {
      e.stopPropagation();
      rejectChange(this.editor, c);
      this.render();
    });

    actions.append(acc, rej);
    el.appendChild(actions);

    // Klik na položku → skoč na změnu v textu.
    el.addEventListener('click', () => {
      this.editor.chain().focus().setTextSelection({ from: c.from, to: c.to }).run();
    });

    return el;
  }

  destroy(): void {
    this.unsub();
    this.root.remove();
  }
}

function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max) + '…' : s;
}

function initialsFrom(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return parts.slice(0, 2).map((p) => p[0].toUpperCase()).join('');
}

/** Barva avataru podle autora — stejná paleta jako u komentářů. */
function authorColor(author: string): string {
  const palette = ['#2b579a', '#c43e1c', '#107c10', '#8764b8', '#c19c00', '#008272', '#a4262c'];
  let hash = 0;
  for (let i = 0; i < author.length; i++) hash = (hash * 31 + author.charCodeAt(i)) >>> 0;
  return palette[hash % palette.length];
}

function pluralChanges(n: number): string {
  if (n === 1) return 'změnu';
  if (n >= 2 && n <= 4) return 'změny';
  return 'změn';
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: '2-digit' });
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
